import { service } from '@maia/customer';
import { IconCreditCard, IconSave } from '@maia/icons';
import { CustomerDto } from '@maia/shared';
import { Button, getErrors, StaticNotification } from '@maia/ui';
import * as v from 'valibot';
import { isIban } from '../../utils/iban/validateIban';
import { useAppForm } from '../form/dynamic-form/dynamic-form';
import { FlattenKeys } from '../../utils/data/flattenKeys.type';

type EditContactFormProps = {
  customer: CustomerDto;
  onSubmit: (value: {
    iban: string;
    cycle: "monthly" | "yearly" | "half-yearly" | "quarterly" | "mixed";
  }) => void;
  isPending: boolean;
  isLoading: boolean;
};

type FormData = Parameters<typeof service.updateBillingCycle>[number]['body'] &
  Parameters<typeof service.updateIban>[number]['body'];

export function EditPaymentForm(props: EditContactFormProps) {
  const { customer, onSubmit, isPending, isLoading } = props;

  const form = useAppForm({
    defaultValues: {
      iban: customer.iban,
      cycle: customer.billingUnit.interval,
    } as FormData,
    onSubmit: ({ value }) => onSubmit(value),
  });

  const requiredFields: Array<FlattenKeys<FormData>> = ['cycle', 'iban'];

  return (
    <div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
        className="grid grid-cols-12 gap-24 col-span-full"
      >
        <form.AppField
          name="iban"
          validators={{
            onChange: v.pipe(
              v.string('Veld is verplicht'),
              v.minLength(1, 'Veld is verplicht'),
              v.custom((value) => {
                if (typeof value !== 'string') {
                  return false;
                }

                return isIban(value);
              }, 'Dit lijkt niet op een geldig IBAN nummer'),
            ),
          }}
        >
          {(field) => {
            return (
              <field.TextField
                label="Rekeningnummer"
                className="col-span-full"
                inputOverrides={{
                  fieldSize: 'large',
                  placeholder: 'Iban',
                  icon: <IconCreditCard />,
                }}
                error={getErrors(field)}
              />
            );
          }}
        </form.AppField>

        <form.AppField
          name="cycle"
          validators={{
            onChange: v.pipe(
              v.union([v.literal('yearly'), v.literal('monthly')]),
              v.minLength(1, 'Veld is verplicht'),
            ),
          }}
        >
          {(field) => {
            return (
              <field.RadioField
                label="Facturatiecyclus"
                className="col-span-full"
                error={getErrors(field)}
                options={[
                  {
                    label: 'Maandelijks',
                    value: 'monthly',
                  },
                  {
                    label: 'Jaarlijks',
                    value: 'yearly',
                  },
                ]}
              />
            );
          }}
        </form.AppField>

        <div className="col-span-full">
          <StaticNotification
            variant="info"
            size="small"
            className="col-span-full"
          >
            <span className="font-medium">Let op!</span> De klant ontvangt een
            e-mail met de bevestiging bij het wijzigen van de facturatiecyclus.
          </StaticNotification>
        </div>

        <div className="col-span-full flex justify-end">
          <form.Subscribe
            selector={(state) => {
              const isFilledIn = requiredFields
                .map((field) => !!state.values[field])
                .every((field) => field);
              return [
                state.canSubmit,
                state.isSubmitting,
                state.isValid,
                isFilledIn,
              ];
            }}
          >
            {([canSubmit, isSubmitting, isValid, isFilledIn]) => (
              <Button
                type="submit"
                intent="cta"
                iconLeft={IconSave}
                isDisabled={
                  !canSubmit ||
                  isSubmitting ||
                  isPending ||
                  !isValid ||
                  !isFilledIn
                }
                isLoading={isLoading}
              >
                Wijziging opslaan
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>
    </div>
  );
}
